// Coach-feedback intent classifier.
//
// Used by the pro-coach review surface (see plan: ben review URL).
// Every voice / typed utterance from a review-mode coach is classified
// asynchronously into one of four kinds:
//
//   action    — the coach is asking the app to do something (plan a
//               session, edit a drill, search…).
//   feedback  — the coach is commenting on the app itself (UX, copy,
//               behaviour) rather than asking it to do anything.
//   hybrid    — both (e.g. "plan a possession session — though I think
//               these phase tags are confusing").
//   question  — the coach is asking how something works (clarification
//               about a feature) rather than acting on it.
//
// Calls Haiku 4.5 via callAIJson with surface 'brain-suggestions' (cheap
// fast lane). Runs async — never blocks the action dispatch. The result
// patches the relevant coach_review_log row.

import { callAIJson } from '@4cm/core/ai';

// ---------------------------------------------------------------------------
// FAST HEURISTIC — pre-dispatch feedback detector
//
// Runs sync before brain.send() so we can short-circuit OBVIOUS feedback
// utterances ("the mic button is too small") without invoking the full
// Brain tool loop. Three signal classes have to match:
//
//   1. Explicit prefix: "feedback:", "by the way", "FYI", "side note"
//      → unambiguous, fire immediately.
//   2. App-meta noun + sentiment: "the [button|menu|page|orb|...] is
//      [too|confusing|clunky|small|...]" → high confidence.
//   3. Hedged opinion + app-meta noun: "I'd prefer the [tab|UI|...]
//      to be [...]" → high confidence.
//
// Ambiguous utterances (no obvious meta signal) fall through to the AI
// classifier path which runs async after Brain replies — labelled in
// the review log without short-circuiting Brain.
// ---------------------------------------------------------------------------

const FEEDBACK_PREFIX_RE = /^\s*(?:feedback|fyi|by\s+the\s+way|side\s+note|just\s+(?:a\s+)?note|heads\s+up|quick\s+note|btw)\b[\s,.:!-]/i;

const APP_META_NOUN_RE = /\b(button|menu|screen|page|tab|ui|ux|app|feature|form|layout|design|interface|click|tap|scroll|loading|wording|copy|label|panel|sidebar|orb|chat|toast|banner|modal|dropdown|prompt|microphone|mic)\b/i;

const SENTIMENT_RE = /\b(too\s+\w+|very\s+\w+|confusing|clunky|annoying|broken|janky|slow|laggy|weird|odd|unclear|hidden|missing|hard\s+to|tricky|fiddly|smooth|great|love|hate|prefer|rather|wish|should\s+(?:be|have|move|stay|happen)|could\s+be|would\s+be|might\s+(?:be|want|look|feel))\b/i;

const HEDGED_OPINION_RE = /\b(i\s+think|i\s+feel|i'?d\s+prefer|i'?d\s+rather|i'?d\s+like|it\s+would\s+be|it'?d\s+be|maybe|perhaps|might\s+be|wish|would\s+love|why\s+is|why\s+does|why\s+doesn'?t|why\s+don'?t)\b/i;

/** Returns true when the utterance is OBVIOUSLY meta-feedback about the
 *  app itself. Conservative — false negatives are fine (the async
 *  classifier still labels them), false positives are bad (we'd
 *  short-circuit a real coaching action). */
export function isObviousFeedback(text: string): boolean {
  const t = text.trim();
  if (t.length < 3) return false;
  // 1. Explicit prefix — unambiguous.
  if (FEEDBACK_PREFIX_RE.test(t)) return true;
  // 2. App-meta noun + sentiment — "the mic button is too small".
  if (APP_META_NOUN_RE.test(t) && SENTIMENT_RE.test(t)) return true;
  // 3. Hedged opinion + app-meta noun — "I'd prefer the orb on the left".
  if (HEDGED_OPINION_RE.test(t) && APP_META_NOUN_RE.test(t)) return true;
  return false;
}


const CLASSIFIER_SYSTEM = `You are classifying a single coach utterance captured during product testing of a soccer coaching app.

The coach is a senior pro using the app for the first time and giving feedback while they use it. They might:
  - Ask the app to do something (action)
  - Comment on the app's UX, copy, or behaviour (feedback)
  - Ask how something works (question)
  - Do both action and feedback in one breath (hybrid)

Classify the utterance into ONE of four kinds. Return STRICT JSON only.

Schema:
{
  "kind": "action" | "feedback" | "hybrid" | "question",
  "confidence": 0.0-1.0,
  "feedback_text": string | null,   // the feedback portion verbatim, null for pure action
  "action_intent": string | null    // a short summary of the action intent, null for pure feedback
}

Decision rules:
  - If the utterance is meta about the APP (mic button too small, this menu is confusing, I'd prefer X, this should be Y): feedback.
  - If the utterance is a direct request the app should fulfil (plan a session, find a drill, schedule training): action.
  - If both are present in one sentence: hybrid, with both fields populated.
  - If the utterance is asking how the app works (where is X, how do I do Y): question.
  - Low confidence (< 0.5) — pick the most likely kind anyway. Confidence is for downstream UX, not a routing gate.

Be terse. The feedback_text should be the literal coach words capturing the feedback (paraphrasing only if necessary for clarity).`;

export interface ClassifiedUtterance {
  kind: 'action' | 'feedback' | 'hybrid' | 'question';
  confidence: number;
  feedback_text: string | null;
  action_intent: string | null;
}

const VALID_KINDS = new Set(['action', 'feedback', 'hybrid', 'question']);

/** Classify an utterance asynchronously. Returns null on failure (caller
 *  should treat as "stay 'pending'"). Fire-and-forget — never blocks. */
export async function classifyCoachUtterance(
  text: string,
  context: { route: string; brainReply?: string | null } = { route: '' },
): Promise<ClassifiedUtterance | null> {
  const clean = text.trim();
  if (!clean) return null;
  try {
    const payload = await callAIJson<Record<string, unknown>>({
      system: CLASSIFIER_SYSTEM,
      userContent: JSON.stringify({
        utterance: clean,
        route: context.route,
        brain_reply: context.brainReply ?? null,
      }),
      maxTokens: 240,
      surface: 'brain-suggestions',
    });
    if (!payload || typeof payload !== 'object') return null;
    const kindRaw = (payload as { kind?: unknown }).kind;
    if (typeof kindRaw !== 'string' || !VALID_KINDS.has(kindRaw)) return null;
    const confidence = typeof (payload as { confidence?: unknown }).confidence === 'number'
      ? Math.max(0, Math.min(1, (payload as { confidence: number }).confidence))
      : 0.5;
    const feedbackText = typeof (payload as { feedback_text?: unknown }).feedback_text === 'string'
      ? ((payload as { feedback_text: string }).feedback_text.trim() || null)
      : null;
    const actionIntent = typeof (payload as { action_intent?: unknown }).action_intent === 'string'
      ? ((payload as { action_intent: string }).action_intent.trim() || null)
      : null;
    return {
      kind: kindRaw as ClassifiedUtterance['kind'],
      confidence,
      feedback_text: feedbackText,
      action_intent: actionIntent,
    };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[coachFeedback] classify failed', err);
    return null;
  }
}
